import { useInfiniteQuery } from "@tanstack/react-query";

import { api } from "@/api";

import { ScheduleRows } from "./ScheduleRows";
import {
  ScheduleEmpty,
  ScheduleError,
  ScheduleSkeleton,
} from "./ScheduleStates";
import { newestResults, RESULTS_PAGE_SIZE } from "./scheduleState";

export function ResultsSchedule() {
  const results = useInfiniteQuery({
    queryKey: ["races", "results", "schedule"],
    queryFn: ({ pageParam }) => api.results(RESULTS_PAGE_SIZE, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last, _pages, _lastParam, pageParams) =>
      last.nextCursor && !pageParams.includes(last.nextCursor)
        ? last.nextCursor
        : undefined,
    refetchInterval: 20_000,
  });
  const races = newestResults(results.data?.pages ?? []);
  return (
    <>
      <p className="schedule-scope">
        Official results, newest first. Load older results below. Upcoming
        window and filters do not apply here.
      </p>
      {results.isPending ? (
        <ScheduleSkeleton />
      ) : results.isError && !results.data ? (
        <ScheduleError
          title="Results could not be loaded"
          retry={() => void results.refetch()}
        />
      ) : (
        <>
          {races.length ? (
            <ScheduleRows
              entries={races.map((race) => ({
                ...race,
                at: race.finishedAt,
                status: "official",
              }))}
            />
          ) : (
            <ScheduleEmpty
              title="No official results yet"
              detail="Results appear here after a race becomes official."
            />
          )}
          {results.isError ? (
            <ScheduleError
              title={
                results.isFetchNextPageError
                  ? "Older results could not be loaded"
                  : "Results could not be refreshed"
              }
              retry={() =>
                void (results.isFetchNextPageError
                  ? results.fetchNextPage()
                  : results.refetch())
              }
            />
          ) : null}
          {results.hasNextPage ? (
            <footer className="schedule-results-footer">
              <button
                className="schedule-button"
                type="button"
                disabled={results.isFetching}
                onClick={() => void results.fetchNextPage()}
              >
                {results.isFetchingNextPage
                  ? "Loading older results…"
                  : "Load older results"}
              </button>
              <span>{races.length} loaded results</span>
            </footer>
          ) : races.length ? (
            <p className="schedule-scope">
              No more published results are available from this feed.
            </p>
          ) : null}
        </>
      )}
    </>
  );
}
